import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { Check, Link } from "lucide-react";
import {
  useMicrosoftAdminConsentUrl,
  useMicrosoftConversationsStatus,
} from "../../hooks/useAccountConversations";

const ConversationsAdminConsentHeader = () => {
  const { companyDomain } = useParams();
  const copyFeedbackTimeoutRef = useRef(null);
  const [adminConsentUrl, setAdminConsentUrl] = useState("");
  const [copyFeedback, setCopyFeedback] = useState("");

  const { data: statusResponse, isLoading } = useMicrosoftConversationsStatus();
  const adminConsentUrlMutation = useMicrosoftAdminConsentUrl();

  const status = statusResponse?.data;
  const returnPath = `/accounts/${encodeURIComponent(companyDomain || "")}/conversations`;

  const isConnectedView =
    Boolean(status?.ready) && !status?.needsConsent && !!companyDomain;
  const shouldShow =
    isConnectedView &&
    status?.needsAdminConsent &&
    status?.canGrantAdminConsent;

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyAdminLink = async () => {
    let url = adminConsentUrl;
    if (!url) {
      const response = await adminConsentUrlMutation.mutateAsync({
        returnPath,
      });
      url = response?.data?.url || "";
      setAdminConsentUrl(url);
    }
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setCopyFeedback("Link copied — send this to your Microsoft 365 admin.");
    } catch {
      setCopyFeedback(url);
    }

    if (copyFeedbackTimeoutRef.current) {
      clearTimeout(copyFeedbackTimeoutRef.current);
    }
    copyFeedbackTimeoutRef.current = setTimeout(() => {
      setCopyFeedback("");
    }, 2500);
  };

  if (isLoading || !shouldShow) return null;

  return (
    <div
      className="flex shrink-0 items-center gap-2"
      title="Transcripts need a one-time Microsoft 365 admin approval for your organization."
    >
      <p className="text-[11px] leading-snug text-gray-500 whitespace-nowrap">
        Microsoft 365 admin approval needed for transcripts
      </p>
      <SecondaryButton
        title={
          <span className="relative inline-flex h-4 min-w-[6.5rem] items-center justify-center">
            <span
              className={`absolute whitespace-nowrap transition-all duration-300 ease-out ${
                !adminConsentUrlMutation.isPending && !copyFeedback
                  ? "translate-y-0 opacity-100"
                  : "-translate-y-1 opacity-0"
              }`}
            >
              Copy consent link
            </span>
            <span
              className={`absolute whitespace-nowrap transition-all duration-300 ease-out ${
                adminConsentUrlMutation.isPending
                  ? "translate-y-0 opacity-100"
                  : "translate-y-1 opacity-0"
              }`}
            >
              Preparing...
            </span>
            <span
              className={`absolute whitespace-nowrap transition-all duration-300 ease-out ${
                copyFeedback && !adminConsentUrlMutation.isPending
                  ? "translate-y-0 opacity-100"
                  : "translate-y-1 opacity-0"
              }`}
            >
              Link copied
            </span>
          </span>
        }
        icon={
          <span className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center">
            <Link
              size={14}
              className={`absolute text-secondary transition-all duration-300 ease-out ${
                copyFeedback
                  ? "scale-50 rotate-45 opacity-0"
                  : "scale-100 rotate-0 opacity-100"
              }`}
            />
            <Check
              size={14}
              className={`absolute text-success-500 transition-all duration-300 ease-out ${
                copyFeedback
                  ? "scale-100 rotate-0 opacity-100"
                  : "scale-50 -rotate-45 opacity-0"
              }`}
            />
          </span>
        }
        className={`h-8 shrink-0 justify-center !px-2.5 !py-1 text-[11px] shadow-none transition-[background-color,border-color,color,transform] duration-300 ease-out active:scale-[0.98] ${
          copyFeedback
            ? "border-success-500 bg-success-50 text-success-500"
            : "border-secondary bg-secondary-light-transparent text-secondary"
        }`}
        disabled={adminConsentUrlMutation.isPending}
        isLoading={adminConsentUrlMutation.isPending}
        onClick={handleCopyAdminLink}
      />
    </div>
  );
};

export default ConversationsAdminConsentHeader;
